"use client";

import * as React from "react";

import { type Brand, GAM_ENV, type VideoPosition } from "@/lib/ads/config";
import { buildTargeting, readAdTest, readChannel, readTier, readUserType } from "@/lib/ads/targeting";
import { useAdRuntime } from "./ad-provider";
import { buildImaTag, videoTargetingFromDom } from "./video-ad-tag";

/**
 * In-stream ads for a plain <video> element via the Google IMA HTML5 SDK.
 *
 *   const ima = useImaAds({ videoRef, containerRef });
 *   // call ima.onPlayRequested() from the play control instead of video.play()
 *   // call ima.reset() when the player is given a new source
 *
 * Schedule = the legacy JW Player / VAST schedule (videoplayer.js on the old sites):
 * - Pre-roll: requested from /Web/<Brand>/<Brand>_Preroll on the first play.
 * - Mid-roll: requested from /Web/<Brand>/<Brand>_Midroll once content playback
 *   passes `midrollAt` seconds (default 60, the legacy cue; one per video).
 * - Post-roll: requested from /Web/<Brand>/<Brand>_Postroll when the content ends
 *   (`postroll`, default on); the content is not restarted afterwards.
 * - The IMA UI speaks the legacy player's language (`locale`, default zh_tw: 廣告,
 *   略過廣告 …); the skip offset stays a creative / VAST setting.
 * - Autoplay parity: the ad request declares whether it will autoplay and whether it
 *   plays muted, and the ad's volume mirrors the content's mute state.
 * - The brand defaults to the route's brand (`useAdRuntime().brand`), so the same
 *   player requests HKEJ_Web_Preroll on /video and EJHealth_Web_Preroll on an EJ
 *   Health article; pass `brand` only to override. `description_url` is the page.
 * - Inert unless the ad runtime is live for this page; any SDK/ad error simply
 *   resumes the content, so a blocked or empty ad never breaks playback.
 *
 * The SDK is loaded once from imasdk.googleapis.com; the ad renders in an overlay
 * div inside `containerRef` on top of the video.
 */

import { loadIma } from "./ima-loader";

export function useImaAds({
  videoRef,
  containerRef,
  brand: brandOverride,
  midrollAt = 60,
  postroll = true,
  locale = "zh_tw",
  autoplayMuted = false,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Defaults to the brand the ad runtime derives from the route. */
  brand?: Brand;
  /** Content second at which the one mid-roll is requested (legacy: 60). */
  midrollAt?: number;
  /** Request a post-roll when the content ends (legacy: yes). */
  postroll?: boolean;
  /** IMA UI locale (legacy player strings were Traditional Chinese). */
  locale?: string;
  /** The host autoplays the content muted, so the first ad request declares muted autoplay. */
  autoplayMuted?: boolean;
}) {
  const rt = useAdRuntime();
  const brand = brandOverride ?? rt.brand;
  const live = rt.enabled && (!rt.pilot);
  const [adPlaying, setAdPlaying] = React.useState(false);
  const state = React.useRef<{ overlay?: HTMLDivElement; loader?: any; manager?: any; display?: any; prerollDone: boolean; midrollDone: boolean; postrollDone: boolean; resumeAfter: boolean }>({ prerollDone: false, midrollDone: false, postrollDone: false, resumeAfter: false });

  const playContent = React.useCallback(() => { void videoRef.current?.play().catch(() => {}); }, [videoRef]);

  /** Request one position; resolves when the ad finished (or failed) and content should resume. */
  const runAd = React.useCallback(async (position: VideoPosition) => {
    const video = videoRef.current, container = containerRef.current;
    if (!video || !container) return;
    const ok = await loadIma(locale);
    const ima = window.google?.ima;
    if (!ok || !ima) return;
    const st = state.current;
    if (!st.overlay) {
      const o = document.createElement("div");
      o.setAttribute("data-ad-format", "Video");
      o.style.cssText = "position:absolute;inset:0;z-index:5;pointer-events:none;";
      container.appendChild(o);
      st.overlay = o;
      st.display = new ima.AdDisplayContainer(o, video);
      st.display.initialize();
      st.loader = new ima.AdsLoader(st.display);
    }
    // Page key-values: what GPT already holds, else the same set the provider computes (a play
    // before gpt.js has set targeting would otherwise send an empty cust_params).
    const kv = { ...buildTargeting(rt.targeting, readUserType(), readChannel(), GAM_ENV || undefined, readTier(), readAdTest()), ...videoTargetingFromDom() };
    const tag = buildImaTag(brand, position, kv, {
      descriptionUrl: typeof window === "undefined" ? undefined : window.location.href.split("#")[0],
    });
    if (!tag) return;
    console.info(`[ads] IMA ${position} request`, tag);
    await new Promise<void>((resolve) => {
      const loader = st.loader;
      const done = () => { st.overlay!.style.pointerEvents = "none"; setAdPlaying(false); resolve(); };
      // Both loader listeners belong to THIS request only; on the error path they used to stay
      // attached and fire again for every later position (three "Preroll error" lines on a post-roll).
      const detach = () => {
        loader.removeEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onLoaded);
        loader.removeEventListener(ima.AdErrorEvent.Type.AD_ERROR, onError);
      };
      const onError = (e?: any) => {
        detach();
        try { const er = e?.getError?.(); if (er) console.warn(`[ads] IMA ${position} error ${er.getErrorCode?.()} (vast ${er.getVastErrorCode?.()}): ${er.getMessage?.()}`); } catch { /* diagnostics only */ }
        st.manager?.destroy?.(); st.manager = undefined; done();
      };
      const onLoaded = (e: any) => {
        detach();
        const settings = new ima.AdsRenderingSettings();
        settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
        const manager = e.getAdsManager(video, settings);
        st.manager = manager;
        manager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onError);
        manager.addEventListener(ima.AdEvent.Type.STARTED, () => console.info(`[ads] IMA ${position} ad started`));
        manager.addEventListener(ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => { video.pause(); st.overlay!.style.pointerEvents = "auto"; setAdPlaying(true); });
        manager.addEventListener(ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => { done(); });
        manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => { manager.destroy(); st.manager = undefined; done(); });
        try {
          // The wrapper can be unlaid-out at the first play; fall back to the video's box.
          const w = container.clientWidth || video.clientWidth || 640, h = container.clientHeight || video.clientHeight || 360;
          manager.init(w, h, ima.ViewMode.NORMAL);
          // The ad mirrors the content's audio state (legacy: muted autoplay → muted ad).
          manager.setVolume(video.muted ? 0 : video.volume);
          manager.start();
        } catch (err) { console.warn("[ads] IMA init failed", err); onError(); }
      };
      loader.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onLoaded, false);
      loader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onError, false);
      const req = new ima.AdsRequest();
      req.adTagUrl = tag;
      req.linearAdSlotWidth = container.clientWidth;
      req.linearAdSlotHeight = container.clientHeight;
      // Pre-roll on a muted-autoplay host, or any mid/post-roll (content already playing): autoplay.
      req.setAdWillAutoPlay(autoplayMuted || position !== "Preroll");
      req.setAdWillPlayMuted(video.muted);
      loader.requestAds(req);
    });
  }, [autoplayMuted, brand, containerRef, locale, videoRef, rt.targeting]);

  /** Call instead of video.play(): runs the pre-roll first on the first play. */
  const onPlayRequested = React.useCallback(async () => {
    if (!live || state.current.prerollDone) { playContent(); return; }
    state.current.prerollDone = true;
    await runAd("Preroll");
    playContent();
  }, [live, playContent, runAd]);

  /* mid-roll cue + post-roll on content end */
  React.useEffect(() => {
    const video = videoRef.current;
    if (!live || !video) return;
    const onTime = async () => {
      if (state.current.midrollDone || video.currentTime < midrollAt) return;
      state.current.midrollDone = true;
      await runAd("Midroll");
      playContent();
    };
    const onEnded = async () => {
      if (!postroll || state.current.postrollDone) return;
      state.current.postrollDone = true;
      await runAd("Postroll");
      // legacy: the post-roll closes the session; the content stays on its last frame
    };
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("ended", onEnded);
    return () => { video.removeEventListener("timeupdate", onTime); video.removeEventListener("ended", onEnded); };
  }, [live, midrollAt, playContent, postroll, runAd, videoRef]);

  /** A new content source starts a new ad session (pre / mid / post again). */
  const reset = React.useCallback(() => {
    const st = state.current;
    st.prerollDone = false; st.midrollDone = false; st.postrollDone = false;
  }, []);

  React.useEffect(() => () => { state.current.manager?.destroy?.(); state.current.overlay?.remove(); }, []);

  return { live, adPlaying, onPlayRequested, reset };
}
